/**
 * Design <-> vector + bounds layer for the high-lift foil optimizer (DESIGN §8 NN-Newton core).
 *
 * A `ParamSpec` is one design DOF with a box `[min,max]` and an optional `locked` flag. The optimizer
 * (inverseDesign / optimize) works on a bare `number[]` of the UNLOCKED values — `packVector` extracts
 * that vector, `unpackVector` writes a stepped vector back (clamping to each box), and `normalize`/
 * `denormalize` map it to/from the unit cube `[0,1]^n` so LM/CMA steps are scale-free. Mixed-inverse =
 * lock every DOF outside the targeted surface region; only the unlocked ones move.
 *
 * Pure, dependency-free.
 */

export interface ParamSpec {
  key: string;
  value: number;
  min: number;
  max: number;
  locked?: boolean;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Indices (into `specs`) of the unlocked DOFs, in order — the layout of every packed vector. */
export function unlockedIndices(specs: ParamSpec[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < specs.length; i++) if (!specs[i]!.locked) out.push(i);
  return out;
}

/** Pack the unlocked values into a flat vector (locked DOFs skipped). */
export function packVector(specs: ParamSpec[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < specs.length; i++) if (!specs[i]!.locked) out.push(specs[i]!.value);
  return out;
}

/** Write a packed vector back onto the unlocked DOFs (clamped to each box); returns a new array. */
export function unpackVector(specs: ParamSpec[], vec: number[]): ParamSpec[] {
  let k = 0;
  return specs.map((s) => {
    if (s.locked) return { ...s };
    const v = clamp(vec[k++]!, s.min, s.max);
    return { ...s, value: v };
  });
}

/** Write a full spec-order vector back onto every DOF (clamped to each box); returns a new array. */
export function unpackFullVector(specs: ParamSpec[], vec: number[]): ParamSpec[] {
  return specs.map((s, i) => ({ ...s, value: clamp(vec[i]!, s.min, s.max) }));
}

/** Clamp every spec's value into its own box (locked included); returns a new array. */
export function clampToBounds(specs: ParamSpec[]): ParamSpec[] {
  return specs.map((s) => ({ ...s, value: clamp(s.value, s.min, s.max) }));
}

/** Map a packed (unlocked) vector to the unit cube [0,1] per-bound (degenerate box -> 0). */
export function normalize(specs: ParamSpec[], vec: number[]): number[] {
  const idx = unlockedIndices(specs);
  return idx.map((i, k) => {
    const s = specs[i]!; const span = s.max - s.min;
    return span > 0 ? clamp((vec[k]! - s.min) / span, 0, 1) : 0;
  });
}

/** Map a unit-cube [0,1] vector back to physical packed values per-bound (clamped to the box). */
export function denormalize(specs: ParamSpec[], unit: number[]): number[] {
  const idx = unlockedIndices(specs);
  return idx.map((i, k) => {
    const s = specs[i]!; const span = s.max - s.min;
    return clamp(s.min + clamp(unit[k]!, 0, 1) * span, s.min, s.max);
  });
}
